import { type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { passwordRecoveryPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityPasswordRecoveryPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getNonNullValues } from '@lib/helpers'
import {
  type BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext } from '@lib/routes'
import {
  defaultModuleQueryParameters,
  getPasswordRecoveryPage,
} from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type PasswordRecoveryPageProps = BasePageProps & {
  page: SanityPasswordRecoveryPage
  site: SanitySiteFragment
}

const PasswordRecoveryPage = ({
  draftMode,
  locale,
  page,
  site,
}: PasswordRecoveryPageProps) => {
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [...videoObjectSchemas]

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={passwordRecoveryPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<PasswordRecoveryPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const passwordRecoveryPage = await getPasswordRecoveryPage(
    sanityClient,
    context.locale,
  )

  if (!passwordRecoveryPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(passwordRecoveryPage.page),
      site: getNonNullValues(passwordRecoveryPage.site),
    },
    revalidate: 60,
  }
}

export default PasswordRecoveryPage
